import { Autocomplete } from "@mui/material";
import { useMemoizedFn } from "ahooks";
import qs from "query-string";
import React from "react";
import { Home, User, Users } from "react-feather";
import { useNavigate } from "react-router";
import { SearchInput, SearchMenu } from ".";
import { MemberMenuItemContent } from "../../../../global/components";
import { useIsMobile } from "../../../../hooks";
import { trackOptionSelect } from "../../../../tracking/search";
import { SearchBookingParam } from "../../api";
import { useSearchParamsQuery } from "../../hooks/useSearchParamsQuery";
import { SearchTab, useSearchState } from "../../state";
import "./styles.css";

interface SearchButtonProps {
  isSearchOpen: boolean;
  setSearchOpen: (isOpen: boolean) => void;
}

const MIN_CHARS_TO_SEARCH = 2;
const CHUNK_SIZE = 120;

type OptionsByTab = Record<SearchTab, SearchBookingParam[]>;

const makeEmptyByTab = (): OptionsByTab =>
  Object.values(SearchTab).reduce((acc, tab) => {
    acc[tab] = [];
    return acc;
  }, {} as OptionsByTab);

export const SearchButton: React.FC<SearchButtonProps> = ({
  isSearchOpen,
  setSearchOpen,
}) => {
  const { activeTab, getFilteredOptions, updateSearchParams } =
    useSearchState();
  const { data: searchBookingParams } = useSearchParamsQuery();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [inputValue, setInputValue] = React.useState<string>("");

  const baseOptions: OptionsByTab = React.useMemo(() => {
    if (!searchBookingParams) return makeEmptyByTab();
    return Object.values(SearchTab).reduce((acc, tab) => {
      acc[tab] = searchBookingParams[tab] ?? [];
      return acc;
    }, {} as OptionsByTab);
  }, [searchBookingParams]);

  const [filteredOptionsAll, setFilteredOptionsAll] =
    React.useState<OptionsByTab>(baseOptions);

  const filter = useMemoizedFn((val: string) => getFilteredOptions(val));

  React.useEffect(() => {
    const q = inputValue.trim();

    if (q.length < MIN_CHARS_TO_SEARCH) {
      setFilteredOptionsAll(baseOptions);
    } else {
      const newFilteredData = filter(q) as OptionsByTab;
      setFilteredOptionsAll(newFilteredData);
    }
  }, [inputValue, baseOptions, filter]);

  const [visibleCountByTab, setVisibleCountByTab] = React.useState<
    Record<SearchTab, number>
  >(() =>
    Object.values(SearchTab).reduce((acc, tab) => {
      acc[tab] = CHUNK_SIZE;
      return acc;
    }, {} as Record<SearchTab, number>)
  );

  React.useEffect(() => {
    setVisibleCountByTab((prev) =>
      Object.values(SearchTab).reduce((acc, tab) => {
        acc[tab] = CHUNK_SIZE;
        return acc;
      }, {} as Record<SearchTab, number>)
    );
  }, [activeTab, inputValue]);

  const activeAll = React.useMemo(
    () => filteredOptionsAll[activeTab] ?? [],
    [filteredOptionsAll, activeTab]
  );
  const activeVisible = React.useMemo(
    () => activeAll.slice(0, visibleCountByTab[activeTab] ?? CHUNK_SIZE),
    [activeAll, activeTab, visibleCountByTab]
  );

  const handleListboxScroll = useMemoizedFn(
    (e: React.UIEvent<HTMLUListElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
      if (nearBottom && activeVisible.length < activeAll.length) {
        setVisibleCountByTab((prev) => ({
          ...prev,
          [activeTab]: Math.min(
            (prev[activeTab] ?? CHUNK_SIZE) + CHUNK_SIZE,
            activeAll.length
          ),
        }));
      }
    }
  );

  const handleOptionChange = useMemoizedFn(
    (_event: unknown, option: string | SearchBookingParam | null) => {
      if (option && typeof option !== "string") {
        const updatedQueryParams = updateSearchParams(option);
        const queryString = qs.stringify(updatedQueryParams);
        trackOptionSelect();
        navigate(`/search-people?${queryString}`);
      }
    }
  );

  const handleInputChange = useMemoizedFn((_e: unknown, value: string) => {
    setInputValue(value ?? "");
  });

  const menuItemIcon = React.useMemo(() => {
    if (activeTab === SearchTab.USER || activeTab === SearchTab.FOLLOWING)
      return <User size={16} />;
    if (activeTab === SearchTab.TEAM) return <Users size={16} />;
    return <Home size={16} />;
  }, [activeTab]);

  const PaperComponent = React.useCallback(
    (props: React.ComponentProps<"div">) => (
      <SearchMenu
        children={props.children}
        filteredOptions={filteredOptionsAll}
      />
    ),
    [filteredOptionsAll]
  );

  return (
    <Autocomplete
      options={activeVisible}
      freeSolo
      filterOptions={(opts) => opts}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      sx={{
        paddingRight: "0px",
        width: isMobile ? "100%" : "280px",
        "&.MuiInputBase-root": { paddingRight: "0px" },
      }}
      value={""}
      inputValue={inputValue}
      disableClearable
      PaperComponent={PaperComponent as any}
      ListboxProps={{
        onScroll: handleListboxScroll,
      }}
      onChange={handleOptionChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <SearchInput
          params={params}
          isSearchOpen={isSearchOpen}
          setSearchOpen={setSearchOpen}
        />
      )}
      renderOption={(props, option) => (
        <MemberMenuItemContent
          elementProps={props}
          name={option.name}
          icon={menuItemIcon}
        />
      )}
      loading={!searchBookingParams}
      noOptionsText={
        inputValue.trim().length < MIN_CHARS_TO_SEARCH ? "" : "Nema rezultata."
      }
    />
  );
};
